import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import type { Theme } from 'webapp/enums/enums';
import { localizeFormatOptionLabels } from 'webapp/utils/number';

import type { OptionRaw } from 'webapp/components/form/select';
import FormSelectWithActionComponent from 'webapp/components/form/select-with-action';
import MetricsPopupCustomFormatComponent from 'webapp/components/metrics/popup-custom-format';

interface Args {
	selected: string;
	options: OptionRaw<string>[];
	onChange: (format: string | null) => void;
	onAdd: (value: string) => void;
	isDisabled?: boolean;
	placeholder?: string;
	isDropdownWider?: boolean;
	theme?: Theme | string;
}

interface FormSelectFormatComponentInterface<T> {
	Element: HTMLSelectElement; // this is needed for ...attributes
	Args: T;
}

export default class FormSelectFormatComponent extends Component<
	FormSelectFormatComponentInterface<Args>
> {
	@service('account') declare accountService: Service.Account;
	@service('popup') declare popupService: Service.Popup;

	@cached
	get displayOptions(): OptionRaw<string>[] {
		return localizeFormatOptionLabels(
			this.args.options,
			this.accountService.isEuropeanNumberFormat,
		);
	}

	get placeholder(): string {
		return this.args.placeholder || 'Format';
	}

	get dropdownAction() {
		const text = 'New custom format';
		return [
			{
				id: null,
				text,
				isSpecial: true,
				position: 'bottom',
				onClick: () => {
					const popup = this.popupService.createModal(MetricsPopupCustomFormatComponent, {
						popupClass: 'popup-custom-format',
					});
					popup.open({
						onSave: (format: string) => {
							this.args.onAdd(format);
						},
					});
				},
				html: `<a class="format-add">${text}</a>`,
			},
		];
	}
	<template>
		<FormSelectWithActionComponent
			...attributes
			@selected={{@selected}}
			@options={{this.displayOptions}}
			@isNumeric={{false}}
			@isMultiple={{false}}
			@isDisabled={{@isDisabled}}
			@placeholder={{this.placeholder}}
			@tooltipOnSelectionFromProperty=""
			@isRequired={{true}}
			@actionOptions={{this.dropdownAction}}
			@isDropdownWider={{@isDropdownWider}}
			@extraClass="dropdown-format"
			@onChange={{@onChange}}
			@theme={{@theme}}
		/>
	</template>
}
